var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { stateKey } from '../config/fieldDefinitionTypes';
import { getDefaultIndicators } from '../formTypes';
import { computeSummarizedValue, hasFormOnlyComponent, parseSummarizedValue, snapshotUnedited, } from '../codec/codecRegistry';
import { makeField } from './fieldFactory';
import { isAddedTicket } from './addableSubfields';
import { configRank, instanceOrder, restoreSubfieldOrder } from './subfieldOrder';
import { isMergedMultiValue, joinTerms } from './mergedMultiValueSubfield';
import { isMultiValueSubfield } from './multiValueSubfield';
import { punctuateBefore, punctuationTriggers } from './positionalPunctuation';
import { buildHiddenSubfieldLookup, instanceHasContent, } from './formConfigEmptyInstanceFilter';
/**
 * CZNA-725 — keep a verbatim field from landing on an index the form just used.
 *
 * <p>`unrepresented` rows keep their original `field_index`, which was safe
 * while every such row belonged to a tag the form emits nothing under (no
 * FieldDef, or a control field). A field instance the form cannot show (M1 in
 * `genericProposalDataToForm`) breaks that: `368` index 0 carrying only `$a`
 * goes verbatim while `368` index 1 is renumbered 0 by the per-tag counter
 * below, and two MARC fields sharing `(tag, field_index)` are ONE field to
 * every reader.
 *
 * <p>So an unrepresented instance whose index collides with one the form
 * emitted moves past everything that tag uses. Non-colliding instances keep
 * their index exactly as before — which is every instance on every record this
 * mattered for until now.
 */
var reindexAgainstForm = function (verbatim, emitted) {
    var formIndices = new Map();
    emitted.forEach(function (row) {
        if (!formIndices.has(row.field_tag))
            formIndices.set(row.field_tag, new Set());
        formIndices.get(row.field_tag).add(row.field_index);
    });
    var taken = new Map();
    verbatim.forEach(function (row) {
        var _a;
        taken.set(row.field_tag, Math.max((_a = taken.get(row.field_tag)) !== null && _a !== void 0 ? _a : -1, row.field_index));
    });
    formIndices.forEach(function (indices, tag) {
        indices.forEach(function (i) { var _a; return taken.set(tag, Math.max((_a = taken.get(tag)) !== null && _a !== void 0 ? _a : -1, i)); });
    });
    var moved = new Map();
    return verbatim.map(function (row) {
        var _a, _b;
        if (!((_a = formIndices.get(row.field_tag)) === null || _a === void 0 ? void 0 : _a.has(row.field_index)))
            return row;
        var key = "".concat(row.field_tag, "#").concat(row.field_index);
        if (!moved.has(key)) {
            var next = ((_b = taken.get(row.field_tag)) !== null && _b !== void 0 ? _b : -1) + 1;
            taken.set(row.field_tag, next);
            moved.set(key, next);
        }
        return __assign(__assign({}, row), { field_index: moved.get(key) }); // eslint-disable-line camelcase
    });
};
/**
 * Converts GenericFormState to ProposalDataField[] using the given FormConfig.
 */
export var genericFormToProposalData = function (config, formData) {
    var _a;
    var result = [];
    // CZNA-657 — passthrough rows owned by a specific field instance, bucketed by
    // the same `${tag}#${field_index}` key the TASK-103 empty-instance filter
    // groups on, so a field-instance the cataloger blanked takes its passthrough
    // with it instead of leaving orphan subfields behind.
    var passthroughByGroup = new Map();
    // CZNA-1410 — the instances whose passthrough the cataloguer added in this form
    // (`isAddedTicket`): every row there was typed, so it keeps the instance alive.
    var addedGroups = new Set();
    // Every instance's key in the order the form walks them.
    var instanceKeys = [];
    // CZNA-1382 — the stored subfield order of the instances that carry one,
    // under the same `${tag}#${field_index}` key.
    var orderByGroup = new Map();
    // CZNA-1412 — per instance, the subfield codes whose list of values differs
    // from what was loaded, with the loaded list: `positionalPunctuation` puts a
    // mark in front of them, and takes it off what they no longer follow. Same key.
    var changedByGroup = new Map();
    // Build a lookup: fieldId (or tag) → FieldDef
    var fieldDefsByKey = new Map();
    for (var _i = 0, _b = config.tabs; _i < _b.length; _i++) {
        var tab = _b[_i];
        for (var _c = 0, _d = tab.fields; _c < _d.length; _c++) {
            var fd = _d[_c];
            var key = stateKey(fd);
            fieldDefsByKey.set(key, fd);
        }
    }
    // Track field_index per MARC tag across fieldId groups (024-isni, 024-wikidata → both tag 024)
    var tagFieldIndexCounter = new Map();
    Object.entries(formData.fields).forEach(function (_a) {
        var formKey = _a[0], instances = _a[1];
        var fieldDef = fieldDefsByKey.get(formKey);
        if (!fieldDef)
            return;
        // CZNA-455: the form never writes a MARC control field (LDR, 00X). They are
        // system-assigned — 001 at approval, 003/005 by the module, 008/LDR by
        // import — and the form only ever displays them. Emitting one here is what
        // produced 22 `proposal_data` rows carrying `subfield_code = 'a'` for 001
        // against 1.29 M `record_data` rows carrying NULL for the same field.
        //
        // Belt and braces on purpose: the compiler already strips a control field's
        // subfields, so the loop below has nothing to iterate and would emit
        // nothing anyway. But that is a consequence of an empty array, not a stated
        // rule — and it was exactly such an incidental mechanism (001 happening not
        // to be registered in react-hook-form) that made this bug look impossible
        // right up until the rows appeared.
        if (fieldDef.control_field)
            return;
        var marcTag = fieldDef.tag; // Always use MARC tag for API, not fieldId
        var _b = getDefaultIndicators(fieldDef), defaultInd1 = _b[0], defaultInd2 = _b[1];
        instances.forEach(function (instance) {
            var _a, _b, _c, _d, _e, _f;
            // Allocate field_index per MARC tag
            var fieldIndex = tagFieldIndexCounter.get(marcTag) || 0;
            tagFieldIndexCounter.set(marcTag, fieldIndex + 1);
            instanceKeys.push("".concat(marcTag, "#").concat(fieldIndex));
            // Read per-instance indicators, fall back to field defaults
            var ind1 = ((_a = instance._ind1) === null || _a === void 0 ? void 0 : _a.value) || defaultInd1;
            var ind2 = ((_b = instance._ind2) === null || _b === void 0 ? void 0 : _b.value) || defaultInd2;
            // CZNA-657 — re-attach the subfields this config does not declare. The
            // ticket rides inside the instance, so the rows follow a reorder and die
            // with a deletion.
            //
            // Two things are re-stamped from the instance rather than kept verbatim,
            // and both are the point of doing this here instead of higher up:
            //   - `field_index`, so the rows follow the instance's CURRENT position;
            //   - the indicators, because they belong to the MARC field, not to the
            //     subfield. Keeping the loaded ones would let one field carry two
            //     indicator values — edit `ind1` on a 550 and its declared `$a` would
            //     say '0' while its preserved `$7` still said ' '. Every reader today
            //     groups by (tag, index) and takes the indicators off the first row,
            //     so nothing visibly breaks, but which row leads the group is
            //     persistence order, not a guarantee: `Record.recordData` has no
            //     `@OrderBy`. If a stale row ever led, the load path would read the
            //     old indicator back and the next save would write it over the edit.
            //
            // `subfield_index` is deliberately NOT re-stamped: it numbers each code's
            // occurrences, and keeping the original keeps a preserved second `$a` the
            // second one instead of collapsing every preserved row onto 0. Where the
            // rows stand among the other codes is `_order` (CZNA-1382).
            var order = instanceOrder(instance);
            if (order) {
                orderByGroup.set("".concat(marcTag, "#").concat(fieldIndex), { order: order, rank: configRank(fieldDef) });
            }
            // CZNA-1410 — a carried row the cataloguer added and left empty is not a
            // subfield: dropped like an empty control. It cannot drop a stored row —
            // the corpus holds no subfield with a blank value (0 of 25 956 785 rows,
            // measured 2026-10-22).
            var ticket = (_c = instance._ptId) === null || _c === void 0 ? void 0 : _c.value;
            if (ticket) {
                var carried = (_f = (_e = (_d = formData.passthrough) === null || _d === void 0 ? void 0 : _d.byInstance) === null || _e === void 0 ? void 0 : _e[ticket]) === null || _f === void 0 ? void 0 : _f.filter(function (row) { var _a; return ((_a = row.value) !== null && _a !== void 0 ? _a : '').trim() !== ''; });
                if (carried && carried.length > 0) {
                    if (isAddedTicket(ticket))
                        addedGroups.add("".concat(marcTag, "#").concat(fieldIndex));
                    passthroughByGroup.set("".concat(marcTag, "#").concat(fieldIndex), carried.map(function (row) { return (__assign(__assign({}, row), { field_index: fieldIndex, indicator1: ind1, indicator2: ind2 })); }));
                }
            }
            fieldDef.subfields.forEach(function (sf) {
                var _a, _b, _c, _d;
                // TASK-115: skip form-only synthetic slots — they never cross the API
                // boundary on their own.
                //
                // CZNA-1007 removed the family-heading branch that used to stand above
                // this loop and emit `$a` itself for `100`/`400` with `ind1='3'`, with
                // its own copy of the unedited guard, its own `summarizeFamilyA` call
                // and its own skip list. The generic path below now does all of it: the
                // codec pair comes from the template (`codecByInd1`), and the one thing
                // that branch really did differently — emit no `components[]` — is
                // `hasFormOnlyComponent` at the emit site.
                if (sf.formOnly)
                    return;
                var subVal = instance[sf.code];
                if (!subVal)
                    return;
                if (isMultiValueSubfield(sf)) {
                    // Multi-value path (TASK-064) — emit one row per entry in `values`,
                    // each with the same field_tag/field_index/subfield_code but
                    // incrementing subfield_index for stable ordering.
                    // Defensive: if the form somehow carries a legacy `value` alongside
                    // `values`, prefer `values` and warn (must not double-emit).
                    if (subVal.value && subVal.values && typeof console !== 'undefined' && console.warn) {
                        console.warn("[genericFormToProposalData] Subfield ".concat(marcTag, "$").concat(sf.code, " has both 'value' and 'values'; preferring 'values'."));
                    }
                    var entries = (subVal.values || [])
                        .map(function (entry) { var _a; return (__assign(__assign({}, entry), { value: ((_a = entry.value) === null || _a === void 0 ? void 0 : _a.trim()) || '' })); })
                        .filter(function (entry) { return entry.value.length > 0; });
                    if (isMergedMultiValue(sf)) {
                        // CZNA-449 — MARC says this subfield is not repeatable, so the terms
                        // go into ONE row (joined) with one component each. Emitting a row
                        // per term produced several <subfield> of a non-repeatable code:
                        // invalid MARC on every export, and a BLOCKING
                        // STRUCT_SUBFIELD_REPEAT that stopped the form from saving at all.
                        if (entries.length === 0)
                            return;
                        var field = makeField(marcTag, sf.code, joinTerms(entries.map(function (e) { return e.value; })), fieldIndex, 0, ind1, ind2);
                        // The FK is subfield-level, so it is only meaningful while the row
                        // stands for a single term; with several terms the row is a
                        // composite and there is no one authority to point at.
                        if (entries.length === 1 && entries[0].sourceAuthorityId) {
                            field.source_authority_id = entries[0].sourceAuthorityId;
                        }
                        field.components = entries.map(function (entry) { return ({
                            component_type: sf.multiValueComponentType,
                            value: entry.value,
                        }); });
                        result.push(field);
                        return;
                    }
                    if (punctuationTriggers(marcTag).has(sf.code)) {
                        var loaded_1 = (_a = subVal.loadedValues) !== null && _a !== void 0 ? _a : [];
                        var changed = entries.length !== loaded_1.length
                            || entries.some(function (entry, i) { return entry.value !== loaded_1[i]; });
                        if (changed) {
                            var groupKey = "".concat(marcTag, "#").concat(fieldIndex);
                            changedByGroup.set(groupKey, ((_b = changedByGroup.get(groupKey)) !== null && _b !== void 0 ? _b : new Map()).set(sf.code, loaded_1));
                        }
                    }
                    // Repeatable subfield (e.g. 100$c / 400$c) — one row per value is
                    // valid MARC, keep them separate.
                    entries.forEach(function (entry, nextSubIdx) {
                        var field = makeField(marcTag, sf.code, entry.value, fieldIndex, nextSubIdx, ind1, ind2);
                        if (entry.sourceAuthorityId) {
                            field.source_authority_id = entry.sourceAuthorityId;
                        }
                        result.push(field);
                    });
                    return;
                }
                if (sf.hasComponents && sf.components) {
                    var compValues = subVal.components || {};
                    // CZNA-1007 — a visible component that never reaches the API means
                    // this subfield's split cannot be written down: `100$a` with
                    // `ind1='3'` composes FAMILY_NAME + FAMILY_QUALIFIER, but the
                    // ontology binds `Family/FAMILY_NAME` to the WHOLE `$a`
                    // (`part: wholeValue`), so emitting our halves as component rows
                    // would contradict it. Text-only round trip via `$a`, exactly as the
                    // hardwired family branch did before this ticket.
                    var componentsAreUnwritable = hasFormOnlyComponent(sf, ind1);
                    // CZNA-310: faithful round-trip for IMPORTED flat component subfields.
                    // `summarizeFn` is LOSSY for several real Aleph shapes (bare year
                    // `"1850"` → `"1850-"`, death-only `"-1951"` → `""`, fuzzy
                    // `"approx. 1900"`, surname-comma `"Novák,"` → `"Novák"`), so the
                    // recomputed summary below would silently rewrite/drop the heading on
                    // a no-op save. When the load fallback stashed the original flat value
                    // in `importedValue` AND the user has NOT edited the derived
                    // components (current components still match the value's
                    // decomposition), re-emit the original flat value verbatim — with no
                    // `components[]`, exactly as it was imported, so further round-trips
                    // stay stable. Once the user edits a component the decomposition no
                    // longer matches and we fall through to the normal recompute path.
                    if (subVal.importedValue !== undefined) {
                        var decomposed = parseSummarizedValue(sf, subVal.importedValue, ind1);
                        var allComponentTypes = new Set(__spreadArray(__spreadArray([], Object.keys(decomposed), true), Object.keys(compValues), true));
                        var unedited = true;
                        // Array.from: the production stripes build target lacks
                        // downlevelIteration, so a Set cannot be iterated directly (TS2802).
                        for (var _i = 0, _e = Array.from(allComponentTypes); _i < _e.length; _i++) {
                            var ct = _e[_i];
                            if ((decomposed[ct] || '') !== (compValues[ct] || '')) {
                                unedited = false;
                                break;
                            }
                        }
                        if (unedited) {
                            var importedTrimmed = subVal.importedValue.trim();
                            // Defensive: an imported empty value would have been dropped on
                            // import; only re-emit a non-empty original.
                            if (importedTrimmed) {
                                var field_1 = makeField(marcTag, sf.code, subVal.importedValue, fieldIndex, 0, ind1, ind2);
                                if (subVal.sourceAuthorityId) {
                                    field_1.source_authority_id = subVal.sourceAuthorityId;
                                }
                                result.push(field_1);
                            }
                            return;
                        }
                    }
                    // CZNA-898 — the unconditional guard. The row arrived WITH persisted
                    // components, so the CZNA-310 passthrough above was never armed
                    // (`hasParsedComponents` is what disarms it) and every save re-emitted
                    // whatever the codec composed. Measured over the live corpus on
                    // 31. 8. 2026 that is not what the corpus holds on 637 353 of 952 440
                    // `100$a` rows (66,9 %) and 79 376 of 130 908 `110$a` rows (60,6 %):
                    // `Kaska, Hynek,` came back `Kaska, Hynek`, `Špilberk (věznice)` came
                    // back `Špilberk`. No edit of any kind was needed.
                    //
                    // So while the cataloguer has not touched this subfield, the stored
                    // row wins over anything recomposed — value AND components, verbatim.
                    // The components half matters on its own: rebuilding `components[]`
                    // from the declared map drops every stored component of an
                    // undeclared type, which on `110$a` is 3 168 `LOCATION`, 1 041
                    // `INSTITUTION_QUALIFIER` and 4 624 second `SUBORDINATE_UNIT` rows.
                    //
                    // "Touched" is decided against the snapshot the load path took, not
                    // against a re-decomposition of the value: the record can answer that
                    // exactly and every heuristic answers it approximately.
                    var persisted = subVal.persisted;
                    if (persisted && snapshotUnedited(persisted.declared, compValues)) {
                        var field_2 = makeField(marcTag, sf.code, persisted.value, fieldIndex, 0, ind1, ind2);
                        if (subVal.sourceAuthorityId) {
                            field_2.source_authority_id = subVal.sourceAuthorityId;
                        }
                        if (persisted.components.length > 0) {
                            field_2.components = persisted.components.map(function (c) { return (__assign({}, c)); });
                        }
                        result.push(field_2);
                        return;
                    }
                    var hasAnyComponent = Object.values(compValues).some(function (v) { return v.trim(); });
                    if (!hasAnyComponent)
                        return;
                    // CZNA-898 — the cataloguer DID edit a component, so there is no
                    // stored value to fall back on and the codec has to produce one. It
                    // still cannot see the ISBD framing that lives in `$a` because of what
                    // stands next to `$a` (the comma introducing `$d`, the qualifier
                    // parenthetical), so the affix peeled off on load is reattached around
                    // the freshly composed core. Without it, correcting a given name
                    // rewrote `Kaska, Hynek,` to `Kaska, Jan` and silently deleted the
                    // punctuation that separates the name from the dates.
                    var summarized = computeSummarizedValue(sf, compValues, persisted, ind1);
                    // Skip emitting a row whose summarize function returned ''.
                    // Suppresses orphan component rows / preview noise.
                    if (!summarized.trim())
                        return;
                    var field = makeField(marcTag, sf.code, summarized, fieldIndex, 0, ind1, ind2);
                    // Preserve sourceAuthorityId if set
                    if (subVal.sourceAuthorityId) {
                        field.source_authority_id = subVal.sourceAuthorityId;
                    }
                    // TASK-115: a single `componentType` may appear in multiple
                    // ComponentDef entries with different `visibleWhen.ind1` (e.g.
                    // FAMILY_NAME has separate labels for surname-led vs family-group
                    // headings). Dedupe by `componentType` to avoid emitting the
                    // same row twice in the API payload.
                    if (componentsAreUnwritable) {
                        result.push(field);
                        return;
                    }
                    var emittedComponents = new Set();
                    field.components = [];
                    // CZNA-1007 — a `formOnly` component is exempt from the binding lock
                    // precisely because it has NO ontology bind, so there is no
                    // `(tag, subfield, componentType)` the backend could store it under.
                    // Dropping it here is unconditional, not a consequence of
                    // `componentsAreUnwritable`: that flag is keyed on the VISIBLE set,
                    // so a qualifier value left behind under `ind1='1'` would otherwise
                    // be emitted as an unbound component row.
                    for (var _f = 0, _g = sf.components.filter(function (c) { return !c.formOnly; }); _f < _g.length; _f++) {
                        var comp = _g[_f];
                        if (!emittedComponents.has(comp.componentType)) {
                            var val = (_c = compValues[comp.componentType]) === null || _c === void 0 ? void 0 : _c.trim();
                            if (val) {
                                field.components.push({
                                    component_type: comp.componentType,
                                    value: val,
                                });
                                emittedComponents.add(comp.componentType);
                            }
                        }
                    }
                    result.push(field);
                }
                else {
                    var val = ((_d = subVal.value) === null || _d === void 0 ? void 0 : _d.trim()) || '';
                    // Emit field if it has a value, OR if it has a defaultValue (hidden
                    // fields) — unless the load path marked that default as one the
                    // incoming record never asserted (CZNA-723, `suppressDefault`).
                    // Without that exception the emptied hidden subfield gets its default
                    // stamped right back here, which is how a 5XX with no `$w` acquired
                    // one on ~67 800 records.
                    if (val || (sf.defaultValue && !subVal.suppressDefault)) {
                        var field = makeField(marcTag, sf.code, val || sf.defaultValue || '', fieldIndex, 0, ind1, ind2);
                        if (subVal.sourceAuthorityId) {
                            field.source_authority_id = subVal.sourceAuthorityId;
                        }
                        // CZNA-1412 — unchanged value, so its stored components still
                        // describe it (see `SubfieldValue.storedComponents`).
                        var stored = subVal.storedComponents;
                        if (stored && val === stored.value.trim()) {
                            field.components = stored.components.map(function (c) { return (__assign({}, c)); });
                        }
                        result.push(field);
                    }
                }
            });
        });
    });
    // TASK-103: drop field-instances where every non-hidden subfield is blank.
    // A field-instance is keyed by (field_tag, field_index). Hidden subfields with
    // a defaultValue (e.g. `024 $2='isni'`, `510 $w='a'`) are ignored when deciding
    // emptiness, so a row that contains only such helper subfields is dropped.
    // Keeps the save path symmetric with MarcPreviewPanel's preview filter.
    var hiddenLookup = buildHiddenSubfieldLookup(config);
    var groups = new Map();
    for (var _e = 0, result_1 = result; _e < result_1.length; _e++) {
        var f = result_1[_e];
        var key = "".concat(f.field_tag, "#").concat(f.field_index);
        var bucket = groups.get(key);
        if (bucket) {
            bucket.push(f);
        }
        else {
            groups.set(key, [f]);
        }
    }
    // CZNA-1410: an added instance the cataloguer filled only outside the template
    // has no declared row to open its group, so the groups are walked in the
    // instances' order — appended last, it would land after the later instances
    // of its tag.
    var filtered = [];
    Array.from(new Set(__spreadArray(__spreadArray([], instanceKeys, true), Array.from(groups.keys()), true))).forEach(function (key) {
        var _a;
        var rows = (_a = groups.get(key)) !== null && _a !== void 0 ? _a : [];
        var tag = key.slice(0, key.indexOf('#'));
        var subfieldLikes = rows.map(function (r) {
            var _a, _b;
            return ({
                subfieldCode: (_a = r.subfield_code) !== null && _a !== void 0 ? _a : '',
                value: (_b = r.value) !== null && _b !== void 0 ? _b : '',
                components: r.components,
            });
        });
        if (addedGroups.has(key) || instanceHasContent(tag, subfieldLikes, hiddenLookup)) {
            // CZNA-657: the instance survived the emptiness filter, so its
            // undeclared subfields ride along. Deliberately INSIDE the surviving
            // branch and deliberately not fed to `instanceHasContent`: a `550` whose
            // visible `$a` the cataloger blanked is a deleted field, and its `$7`
            // must not keep it alive — nor be left behind once it goes. Rows the
            // cataloguer typed under an added ticket are the exception (CZNA-1410):
            // dropping them would discard input, not a leftover.
            var carried = passthroughByGroup.get(key);
            var own = carried ? rows.concat(carried) : rows;
            // CZNA-1382: the backend stores the order of this list as the order of
            // the subfields, so it is the place to put them back in it.
            var ordering = orderByGroup.get(key);
            var ordered = ordering
                ? restoreSubfieldOrder(own, ordering.order, ordering.rank, new Set(carried !== null && carried !== void 0 ? carried : []))
                : own;
            // CZNA-1412: after the order is final, because the mark depends on which
            // subfield comes NEXT.
            var changed = changedByGroup.get(key);
            filtered.push.apply(filtered, (changed ? punctuateBefore(tag, ordered, changed) : ordered));
        }
    });
    // CZNA-657: rows whose whole field the form never rendered. They are outside
    // the emptiness filter on purpose — a control field carries no subfield at
    // all, so `instanceHasContent` would read it as blank and drop it, which is
    // exactly the deletion this ticket exists to stop.
    var unrepresented = (_a = formData.passthrough) === null || _a === void 0 ? void 0 : _a.unrepresented;
    if (unrepresented && unrepresented.length > 0) {
        filtered.push.apply(filtered, reindexAgainstForm(unrepresented, filtered));
    }
    return filtered;
};
